import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { AfterimagePass } from 'three/examples/jsm/postprocessing/AfterimagePass.js';
import { VISUAL_SETTINGS } from '../constants.js';

export function createPostProcessing(scene, camera, renderer, controls) {
  const composer = new EffectComposer(renderer);
  composer.userData = composer.userData ?? {};
  const renderScene = new RenderPass(scene, camera);
  const afterImagePass = new AfterimagePass();

  afterImagePass.uniforms.damp.value = VISUAL_SETTINGS.afterimageDamp;

  composer.addPass(renderScene);
  composer.addPass(afterImagePass);

  if (controls) {
    const setAfterimageDamp = (damp) => {
      afterImagePass.uniforms.damp.value = damp;
    };

    const clearRecoveryTimeout = () => {
      if (composer.userData.recoveryTimeoutId != null) {
        window.clearTimeout(composer.userData.recoveryTimeoutId);
        composer.userData.recoveryTimeoutId = null;
      }
    };

    const pulseAfterimageRecovery = () => {
      clearRecoveryTimeout();
      setAfterimageDamp(VISUAL_SETTINGS.afterimageInteractionDamp);
      composer.userData.recoveryTimeoutId = window.setTimeout(() => {
        composer.userData.recoveryTimeoutId = null;

        if (!composer.userData.interacting) {
          setAfterimageDamp(VISUAL_SETTINGS.afterimageDamp);
        }
      }, VISUAL_SETTINGS.afterimageRecoveryMs);
    };

    const onInteractionStart = () => {
      composer.userData.interacting = true;
      clearRecoveryTimeout();
      setAfterimageDamp(VISUAL_SETTINGS.afterimageInteractionDamp);
    };

    const onInteractionEnd = () => {
      composer.userData.interacting = false;
      pulseAfterimageRecovery();
    };

    composer.userData.interacting = false;
    composer.userData.controls = controls;
    composer.userData.pulseAfterimageRecovery = pulseAfterimageRecovery;
    composer.userData.clearRecoveryTimeout = clearRecoveryTimeout;
    composer.userData.onInteractionStart = onInteractionStart;
    composer.userData.onInteractionEnd = onInteractionEnd;
    composer.userData.setAfterimageDamp = setAfterimageDamp;

    controls.addEventListener('start', onInteractionStart);
    controls.addEventListener('end', onInteractionEnd);
  }

  return composer;
}

export function clearPostProcessing(composerRef) {
  const composer = composerRef.current;
  if (!composer) {
    return;
  }

  composer.passes.forEach((pass) => {
    if (pass.dispose) {
      pass.dispose();
    }
  });

  composer.renderTarget1?.dispose();
  composer.renderTarget2?.dispose();
  composer.userData.clearRecoveryTimeout?.();
  composer.userData.controls?.removeEventListener('start', composer.userData.onInteractionStart);
  composer.userData.controls?.removeEventListener('end', composer.userData.onInteractionEnd);
  composerRef.current = null;
}

export function resizePostProcessing(composer, width, height) {
  composer?.setSize(width, height);
}

export function resetPostProcessingTrails(composer) {
  composer?.userData?.pulseAfterimageRecovery?.();
}
